import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Plus, ImagePlus, ChevronUp, ChevronDown, Save } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { photoUrl } from "@/components/site/Menus";
import ImageCropModal from "@/components/admin/ImageCropModal";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const inp = "w-full bg-background border-2 border-foreground/30 rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-foreground";

function Row({ item, onChange, onDelete, onUp, onDown, onPhoto }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const url = photoUrl(item.photo_path);
  return (
    <div ref={setNodeRef} style={style} data-testid={`menu-row-${item.id}`}
      className="bg-background border-2 border-foreground rounded-xl p-3 flex gap-3 items-start">
      <button {...attributes} {...listeners} className="mt-1 cursor-grab text-foreground/40 hover:text-foreground" data-testid={`drag-${item.id}`}><GripVertical size={18} /></button>
      <button onClick={() => onPhoto(item)} data-testid={`photo-${item.id}`}
        className="shrink-0 w-14 h-14 rounded-lg border-2 border-dashed border-foreground/40 overflow-hidden flex items-center justify-center hover:border-foreground">
        {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={16} className="text-foreground/40" />}
      </button>
      <div className="flex-1 space-y-1.5 min-w-0">
        <div className="flex gap-2">
          <input className={inp} placeholder="Item name" value={item.name} onChange={(e) => onChange(item.id, "name", e.target.value)} data-testid={`name-${item.id}`} />
          <input className={`${inp} w-20 shrink-0`} placeholder="$" value={item.price} onChange={(e) => onChange(item.id, "price", e.target.value)} data-testid={`price-${item.id}`} />
        </div>
        <input className={inp} placeholder="Description" value={item.desc} onChange={(e) => onChange(item.id, "desc", e.target.value)} data-testid={`desc-${item.id}`} />
        <input className={inp} placeholder="Tags (V, VO, GFO) — comma separated" value={(item.tags || []).join(", ")}
          onChange={(e) => onChange(item.id, "tags", e.target.value.split(",").map((t) => t.trim().toUpperCase()).filter(Boolean))} data-testid={`tags-${item.id}`} />
      </div>
      <div className="flex flex-col gap-1">
        <button onClick={() => onUp(item)} className="p-1 border-2 border-foreground/30 rounded hover:bg-muted"><ChevronUp size={13} /></button>
        <button onClick={() => onDown(item)} className="p-1 border-2 border-foreground/30 rounded hover:bg-muted"><ChevronDown size={13} /></button>
        <button onClick={() => onDelete(item.id)} data-testid={`delete-${item.id}`} className="p-1 border-2 border-destructive text-destructive rounded hover:bg-destructive hover:text-destructive-foreground"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function MenuEditor() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const cropTargetRef = useRef(null);
  const fileRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    const { data } = await api.get("/menu");
    const flat = [];
    (data.categories || []).forEach((c) => (data.items[c] || []).forEach((it) => flat.push({ ...it })));
    setItems(flat);
    setCats(data.categories || []);
    setCat((prev) => prev || data.categories?.[0] || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = items.filter((i) => i.category === cat);

  const change = (id, field, val) => setItems((its) => its.map((i) => (i.id === id ? { ...i, [field]: val } : i)));
  const del = (id) => setItems((its) => its.filter((i) => i.id !== id));
  const add = () => setItems((its) => [...its, { id: uid(), category: cat, name: "", desc: "", price: "", tags: [], photo_path: null }]);

  const reorderCurrent = (newCurrent) => {
    setItems((its) => {
      const others = its.filter((i) => i.category !== cat);
      // preserve original relative position of the block: keep others, append reordered current at their spots by category grouping
      const result = [];
      const seenCat = new Set();
      its.forEach((i) => {
        if (i.category === cat) {
          if (!seenCat.has(cat)) { seenCat.add(cat); result.push(...newCurrent); }
        } else result.push(i);
      });
      return result.length ? result : [...others, ...newCurrent];
    });
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = current.map((i) => i.id);
    reorderCurrent(arrayMove(current, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  const move = (item, dir) => {
    const idx = current.findIndex((i) => i.id === item.id);
    const j = idx + dir;
    if (j < 0 || j >= current.length) return;
    reorderCurrent(arrayMove(current, idx, j));
  };

  const pickPhoto = (item) => { cropTargetRef.current = item.id; fileRef.current.click(); };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(f);
  };
  const onCropped = async (blob) => {
    const id = cropTargetRef.current;
    setCropSrc(null);
    const fd = new FormData();
    fd.append("file", blob, "menu.jpg");
    try {
      const { data } = await api.post("/admin/menu/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      change(id, "photo_path", data.photo_path);
      toast.success("Photo added");
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((it, i) => ({ ...it, order: i }));
      await api.put("/admin/menu", { items: payload });
      toast.success("Menu saved & live on the site");
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="menu-file-input" />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`edit-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-head font-bold text-sm px-4 py-2 rounded-full border-2 border-foreground transition-colors ${cat === c ? "bg-foreground text-background" : "hover:bg-muted"}`}>{c}</button>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="menu-save"
          className="flex items-center gap-2 font-head font-bold bg-secondary text-secondary-foreground border-2 border-foreground rounded-full px-5 py-2.5 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
          <Save size={16} /> {saving ? "Saving…" : "Save menu"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* Editor */}
        <div className="space-y-3">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={current.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              {current.map((item) => (
                <Row key={item.id} item={item} onChange={change} onDelete={del} onUp={(i) => move(i, -1)} onDown={(i) => move(i, 1)} onPhoto={pickPhoto} />
              ))}
            </SortableContext>
          </DndContext>
          <button onClick={add} data-testid="menu-add-item"
            className="w-full flex items-center justify-center gap-2 font-head font-bold border-2 border-dashed border-foreground rounded-xl py-3 hover:bg-muted transition-colors">
            <Plus size={16} /> Add item to {cat}
          </button>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-24">
          <p className="overline text-foreground/60 mb-3">Live preview · {cat}</p>
          <div className="bg-foreground text-background rounded-2xl p-6">
            <h3 className="font-head font-black text-2xl mb-4">{cat}</h3>
            <div className="divide-y divide-background/20">
              {current.map((item) => {
                const url = photoUrl(item.photo_path);
                return (
                  <div key={item.id} className="flex items-center gap-3 py-3">
                    {url && <img src={url} alt="" className="w-12 h-12 rounded object-cover border border-background/30" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-head font-bold">{item.name || "Untitled"}</span>
                        {(item.tags || []).map((t) => <span key={t} className="font-mono-plex text-[9px] border border-primary text-primary rounded px-1">{t}</span>)}
                      </div>
                      {item.desc && <p className="text-background/60 text-xs mt-0.5">{item.desc}</p>}
                    </div>
                    {item.price && <span className="font-mono-plex text-primary">${item.price}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {cropSrc && <ImageCropModal imageSrc={cropSrc} onCancel={() => setCropSrc(null)} onCropped={onCropped} />}
    </div>
  );
}
